import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

export default function ClientLedger() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/revenue/clients").then(({ data }) => setClients(data)).catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  useEffect(() => {
    if (!clientId) { setLedger(null); return; }
    setLoading(true);
    api.get(`/revenue/clients/${clientId}/ledger`)
      .then(({ data }) => setLedger(data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [clientId]);

  return (
    <div className="space-y-6" data-testid="client-ledger-page">
      <PageHeader title="Client Ledger" description="Outstanding receivable and ageing across RA bills for a client." />
      <div className="max-w-xs">
        <Label>Client</Label>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger data-testid="ledger-client-select" className="mt-1.5"><SelectValue placeholder="Select a client" /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!clientId ? (
        <EmptyState title="Select a client to view their ledger" testId="client-ledger-empty-state" />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : ledger ? (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="mt-1 font-mono text-lg font-bold text-foreground" data-testid="ledger-outstanding">{ledger.outstanding}</p>
            </div>
            {ledger.aging.map((bucket) => (
              <div key={bucket.label} className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{bucket.label} days</p>
                <p className="mt-1 font-mono text-lg font-semibold text-foreground">{bucket.amount}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card">
            {ledger.entries.length === 0 ? (
              <EmptyState title="No posted RA bills for this client yet" />
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {ledger.entries.map((e) => (
                    <TableRow key={e.doc_id} data-testid={`ledger-entry-row-${e.number}`}>
                      <TableCell className="text-muted-foreground">{e.doc_type.replaceAll("_", " ")}</TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{e.number}</TableCell>
                      <TableCell className="text-muted-foreground">{e.doc_date}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-foreground">{e.debit}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-foreground">{e.credit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
